/**
 * Busy-mode lead-time policy.
 *
 * Purpose: decide which lead time (in minutes) applies to a pickup/delivery slot when a
 * merchant has temporarily raised its lead time ("busy mode").
 *
 * Inputs: the merchant's current `leadTime` primitive plus, when busy mode is configured,
 * a `BusyModeFields` pair sourced from the merchant DTO via `toBusyModeFields`.
 * Output: a single effective lead time in minutes.
 *
 * Safe configuration points: `busyModeExpireTime` (epoch millis) is the only clock-sensitive
 * value; it is always compared against a caller-supplied `nowMillis`, so this module never
 * reads the system clock. `defaultLeadTime` is what busy mode falls back to once it expires —
 * `0` is a valid configuration and is preserved.
 */
import { Merchant } from "@/dto/merchant";

/** Present only when the merchant has busy mode configured. Both fields required together. */
export interface BusyModeFields {
  defaultLeadTime: number;
  busyModeExpireTime: number;
}

const isValidNumber = (value: number | null | undefined): value is number =>
  typeof value === "number";

/**
 * Narrows a merchant's independently-nullable busy-mode fields into the invariant-carrying
 * object. Single home for the null-checking, so no call site repeats it: an `undefined`
 * result means "busy mode not configured".
 */
export const toBusyModeFields = (
  merchant: Merchant
): BusyModeFields | undefined =>
  isValidNumber(merchant?.defaultLeadTime) &&
  isValidNumber(merchant?.busyModeExpireTime)
    ? {
        defaultLeadTime: merchant.defaultLeadTime,
        busyModeExpireTime: merchant.busyModeExpireTime,
      }
    : undefined;


/**
  * Returns the effective lead time (in minutes) for a merchant's pickup/delivery slot
 * @param leadTime The lead time (in minutes) for the merchant's pickup/delivery slot
 * @param busyModeFields The merchant's busy mode fields, if configured
 * @param atMillis The timestamp (in milliseconds) at which to evaluate the effective lead time
 */
export const getEffectiveLeadTime = (
  leadTime: number,
  busyModeFields: BusyModeFields | undefined,
  atMillis: number,
): number => {
  // Busy mode is not configured for this merchant, so the plain lead time applies.
  if (!busyModeFields) {
    return leadTime;
  }

  const { defaultLeadTime, busyModeExpireTime } = busyModeFields;

  return atMillis < busyModeExpireTime ? leadTime : defaultLeadTime;
};
